import React from "react";
import { ScrollView } from "react-native";
import { LinkedFileItem } from "./LinkedFileItem";
import { LinkedImageItem } from "./LinkedImageItem";
import { LINKED_ITEM_CONTAINER_HEIGHT } from "./LinkedItemContainer";
import { graphql, useFragment } from "react-relay";
import { Box, ColorType, useLayoutContext } from "arkh";
import { LinkedContent_images$key } from "./__generated__/LinkedContent_images.graphql";
import { LinkedContent_files$key } from "./__generated__/LinkedContent_files.graphql";
import { useLightboxControls } from "@/src/common/lightbox/LightboxProvider";

function LinkedContentContainer(props: { children: React.ReactNode }) {
  const layout = useLayoutContext();
  return (
    <ScrollView
      style={{
        maxHeight: LINKED_ITEM_CONTAINER_HEIGHT * 2 + layout.space.medium * 3,
      }}
      contentContainerStyle={{
        paddingHorizontal: layout.space.medium,
        paddingBottom: layout.space.medium,
        flexDirection: "row",
        flexWrap: "wrap",
      }}
    >
      {props.children}
    </ScrollView>
  );
}

export function LinkedContent(props: {
  images: LinkedContent_images$key;
  files: LinkedContent_files$key;
  openFile: (file: {
    fileId: string;
    fileName: string;
    fileType: string;
    url: string;
  }) => void;
  removeImage?: (id: string) => void;
  removeFile?: (id: string) => void;
  borderColor?: ColorType;
  disabled?: boolean;
}) {
  const images = useFragment(
    graphql`
      fragment LinkedContent_images on Image @relay(plural: true) {
        id
        sizes {
          height
          width
        }
        ...Image_image
        ...LinkedImageItem_image
      }
    `,
    props.images
  );

  const files = useFragment(
    graphql`
      fragment LinkedContent_files on File @relay(plural: true) {
        id
        name
        type
        url
        ...LinkedFileItem_file
      }
    `,
    props.files
  );

  const lightbox = useLightboxControls();
  return (
    <>
      <LinkedContentContainer>
        {images.map((image, index) => (
          <ItemContainer key={image.id}>
            <LinkedImageItem
              image={image}
              key={image.id}
              onRemove={props.disabled ? undefined : props.removeImage}
              openImage={() => {
                lightbox.openLightbox({
                  index,
                  images: images.map((i) => ({
                    image: {
                      type: "remote" as const,
                      image: i,
                    },
                    onDelete: props.disabled
                      ? undefined
                      : () => {
                          props.removeImage?.(i.id);
                        },
                    mediaType: "image" as const,
                    dimensions: {
                      width: i.sizes?.[0]?.width ?? 1,
                      height: i.sizes?.[0]?.height ?? 1,
                    },
                  })),
                });
              }}
              borderColor={props.borderColor}
            />
          </ItemContainer>
        ))}
        {files.map((file) => (
          <ItemContainer key={file.id}>
            <LinkedFileItem
              file={file}
              key={file.id}
              borderColor={props.borderColor}
              onPress={() => {
                props.openFile({
                  fileId: file.id,
                  fileName: file.name ?? "",
                  fileType: file.type ?? "",
                  url: file.url,
                });
              }}
              onUnlink={
                !props.disabled && props.removeFile
                  ? () => {
                      props.removeFile?.(file.id);
                    }
                  : undefined
              }
            />
          </ItemContainer>
        ))}
      </LinkedContentContainer>
    </>
  );
}

function ItemContainer(props: { children: React.ReactNode }) {
  return (
    <Box spaceTop="medium" spaceRight="medium">
      {props.children}
    </Box>
  );
}
